// Lecturas y escrituras de reservas en Firebase. Son las mismas rutas y los mismos pasos que la app:
//   coordiph/lib/function/firebase.dart (crearReserva, aprobarSolicitud, rechazarSolicitud,
//   anularReserva, ajustarContadoresReserva, validarAforo, cargarReservasCalendario,
//   cargarSolicitudes, cargarSolicitudesAprobadas, cargarZona, crearZona, crearPaseVisitante)
//   coordiph/lib/function/backend.dart (aprobarReserva / rechazarReserva → correo al residente)
// En la app las rutas llevan `${prefs.id}`, que siempre está vacío; por eso aquí no aparece.
import { get, ref, set, push, remove } from "firebase/database";
import { db } from "./api_firebase";
import { crearLog } from "../pages/components/logs/logs";
import { reservaAJson, correoClave, urlsConteo, calcularAforo, fechaHoraDart } from "./reservas_reglas";

const URL_NOTIFICAR = "https://coordiph.herokuapp.com/reservas/notificarreserva";
const raiz = (codigo) => `coordi_ph_clientes/${codigo}`;

// A diferencia de consultarSimple (que devuelve {}), aquí "no existe" es null, como en la app.
export const leer = async (ruta) => {
  const snap = await get(ref(db, ruta));
  if (!snap.exists()) return null;
  crearLog(ruta, "leer");
  return snap.val();
};
// Equivale a obtenerUnicoResultado(): el valor como texto, o null.
export const leerTexto = async (ruta) => {
  const v = await leer(ruta);
  return v === null ? null : String(v);
};
const escribir = async (ruta, valor) => { await set(ref(db, ruta), valor); crearLog(ruta, "actualizar"); };
const crear = async (ruta, valor) => {
  const nuevo = push(ref(db, ruta));
  await set(nuevo, valor);
  crearLog(ruta, "crear");
  return nuevo.key;
};
const borrar = async (ruta) => { await remove(ref(db, ruta)); crearLog(ruta, "eliminar"); };

export const obtenerIdConjunto = async (codigo) => leerTexto(`clientes/${codigo}`);

export const cargarConjunto = async (codigo) => {
  const id = await obtenerIdConjunto(codigo);
  if (!id) return null;
  const datos = await leer(`${raiz(codigo)}/${id}`);
  return datos ? { ...datos, idConjunto: id } : null;
};

// Configuración de una zona: coordi_ph_clientes/{codigo}/{zonaId}
export const cargarZona = (codigo, zonaId) => leer(`${raiz(codigo)}/${zonaId}`);
export const crearZona = (codigo, zonaId, datos) => escribir(`${raiz(codigo)}/${zonaId}`, datos);

// Reglas que la app le pone a una zona recién habilitada (seleccionar_zonas.dart → _crearZona).
export const ZONA_POR_DEFECTO = {
  reservaUnica: true, contarPendientes: true, DepositoBool: false, DepositoValor: 0,
  HoraFinal: "22:00", HoraInicio: "06:00", LimiteHoras: 1, LimitePersonas: 1,
  ObservacionesYRecomendaciones: "", RequiereAutorizacion: false, requiereAforo: false,
  cantidadSedes: 1, solicitarDatosVisitantes: false,
};

// Usuario (para el bloqueo por mora): correos/{correo} → id → usuarios/{correo}/{id}
export const cargarUsuario = async (correo) => {
  const clave = correoClave(correo).replace(/ /g, "");
  const id = await leerTexto(`correos/${clave}`);
  if (!id) return null;
  return leer(`usuarios/${clave}/${id}`);
};

// Meses que muestra el calendario: el anterior, el actual y los que faltan del año. En diciembre
// se agrega enero para no quedar sin mes siguiente. Ej.: en octubre → sep, oct, nov y dic.
// Igual que mesesCalendarioReservas en firebase.dart.
export const mesesCalendarioReservas = (ahora) => {
  const mesActual = ahora.getMonth() + 1;
  const ultimoMes = mesActual === 12 ? 13 : 12;
  const meses = [];
  for (let m = mesActual - 1; m <= ultimoMes; m++) {
    meses.push(new Date(ahora.getFullYear(), m - 1, 1));
  }
  return meses;
};

// Calendario: índice reserva_mes de cada mes → reservas/{estado}/{id}
export const cargarReservasCalendario = async (codigo, zonaId) => {
  const cargarMes = async (f) => {
    const indice = await leer(`${raiz(codigo)}/reserva_mes/${zonaId}/${f.getFullYear()}/${String(f.getMonth() + 1).padStart(2, "0")}`);
    if (!indice || typeof indice !== "object") return [];
    const res = await Promise.all(Object.entries(indice).map(async ([id, estado]) => {
      const data = await leer(`${raiz(codigo)}/reservas/${estado}/${id}`);
      return data ? { ...reservaAJson(data), id } : null;
    }));
    return res.filter(Boolean);
  };
  // Los meses se consultan al mismo tiempo para que el calendario abra rápido.
  const porMes = await Promise.all(mesesCalendarioReservas(new Date()).map(cargarMes));
  return porMes.flat();
};

// Autorizaciones: reservas/pendientes sin las anuladas
export const cargarSolicitudes = async (codigo) => {
  const datos = await leer(`${raiz(codigo)}/reservas/pendientes`);
  if (!datos) return [];
  return Object.entries(datos)
    .map(([id, v]) => ({ ...reservaAJson(v), id }))
    .filter(r => r.estado !== "Anulado");
};

// reservas/{estado} filtradas por sede ("todo" trae todas). Marca urlReserva con el estado.
export const cargarSolicitudesAprobadas = async (codigo, sede, estado) => {
  const datos = await leer(`${raiz(codigo)}/reservas/${estado}`);
  if (!datos) return [];
  const sedesValidas = new Set(["null", null, undefined, "", "0", "1"]);
  return Object.entries(datos)
    .map(([id, v]) => ({ ...reservaAJson(v), id, urlReserva: estado }))
    .filter(r => r.sedeSeleccionada === "todo" || sedesValidas.has(r.sedeSeleccionada) || r.sedeSeleccionada === sede)
    .reverse();
};

// Horas libres para una zona única: aprobados/{zona}/{fecha}
export const validarAforo = async (codigo, zonaId, fecha, horas, sede) => {
  try {
    const datos = await leer(`${raiz(codigo)}/aprobados/${zonaId}/${fecha}`);
    return calcularAforo(horas, Object.values(datos || {}), sede);
  } catch {
    return { libres: [], aforo: Object.fromEntries(horas.map(h => [h, 0])) };
  }
};

// Contadores de cupo (conteo_reservas): suma o resta sin bajar de 0. Solo toca los que existen.
export const ajustarContadoresReserva = async (codigo, reserva, delta) => {
  if (!reserva.correo) return;
  const urls = urlsConteo(reserva.correo, codigo, reserva.zonaId, reserva.fecha);
  if (!urls) return;
  for (const url of [urls.dia, urls.semana, urls.mes]) {
    const actual = parseInt(await leerTexto(url) ?? "", 10);
    if (Number.isNaN(actual)) continue;
    const nuevo = actual + delta < 0 ? 0 : actual + delta;
    if (nuevo !== actual) await escribir(url, String(nuevo));
  }
};

// crear_reserva.dart → _obtenerConteo(): la app usa int.parse, que falla con valores que no son
// números; aquí un valor dañado (p. ej. "NaN") se trata como 0 para no bloquear la reserva.
export const obtenerConteo = async (url) => {
  const v = await leerTexto(url);
  const n = v !== null && v !== "" && v !== "null" ? parseInt(v, 10) : 0;
  return Number.isNaN(n) ? 0 : n;
};

// crear_reserva.dart → _motivosCuota()
export const motivosCuota = async (codigo, correo, zonaId, fecha, permisosZona) => {
  if (!permisosZona) return [];
  const urls = urlsConteo(correo, codigo, zonaId, fecha);
  if (!urls) return [];
  const motivos = [];
  for (const [limite, clave, etiqueta] of [[permisosZona.reservaPorDia, "dia", "día"], [permisosZona.reservaPorSemana, "semana", "semana"], [permisosZona.reservaPorMes, "mes", "mes"]]) {
    if (limite === null || limite === undefined || limite <= 0) continue;
    if (await obtenerConteo(urls[clave]) >= limite) motivos.push(`Ya alcanzó el límite de ${limite} reserva(s) por ${etiqueta}.`);
  }
  return motivos;
};

// crear_reserva.dart → _incrementarContadores() (solo residentes, después de guardar)
export const incrementarContadores = async (codigo, correo, zonaId, fecha, permisosZona) => {
  if (!permisosZona) return;
  const urls = urlsConteo(correo, codigo, zonaId, fecha);
  if (!urls) return;
  for (const [limite, clave] of [[permisosZona.reservaPorDia, "dia"], [permisosZona.reservaPorSemana, "semana"], [permisosZona.reservaPorMes, "mes"]]) {
    if (limite === null || limite === undefined || limite <= 0) continue;
    await escribir(urls[clave], String(await obtenerConteo(urls[clave]) + 1));
  }
};

// Correo de aprobación o rechazo (backend.dart → aprobarReserva / rechazarReserva)
export const notificarReserva = async ({ correo, codigo, idConjunto, tipo, fecha, desde, hasta, motivo, zona }) => {
  try {
    const data = { mail: correo, codigoCliente: codigo, idConjunto, tipo, fecha, desde, hasta, zona };
    if (tipo === "rechazado") data.motivo = motivo;
    await fetch(URL_NOTIFICAR, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) });
  } catch (e) {
    console.warn("No se pudo enviar el correo de la reserva:", e);
  }
};

// firebase.dart → crearReserva(). `reserva` ya trae estado, zona, fecha, horas, etc.
// Devuelve el id nuevo (la clave que Firebase da en la copia por zona).
export const crearReserva = async ({ codigo, correoSesion, idConjunto, reserva }) => {
  const zona = reserva.zonaId;
  if (!reserva.fechaCreacionRegistro) reserva.fechaCreacionRegistro = fechaHoraDart();

  const id = await crear(`${raiz(codigo)}/${zona}/reservas`, reservaAJson(reserva));
  if (!id) throw new Error("No se pudo guardar la reserva");
  reserva.id = id;
  const json = reservaAJson(reserva);

  await escribir(`usuarios/${correoClave(correoSesion)}/reservas/${zona}/${id}`, json);
  if (reserva.estado === "Pendiente") {
    await escribir(`${raiz(codigo)}/reservas/pendientes/${id}`, json);
  } else {
    await escribir(`${raiz(codigo)}/reservas/aprobados/${id}`, json);
    await escribir(`${raiz(codigo)}/aprobados/${reserva.zonaId}/${reserva.fecha}/${id}`, json);
    notificarReserva({ correo: reserva.correo, codigo, idConjunto, tipo: "aprobado", fecha: reserva.fecha, desde: reserva.horaInicio, hasta: reserva.horaFinal, zona: reserva.zona });
  }
  return id;
};

// Índices que usa el calendario (crear_reserva.dart, después de guardar)
export const escribirIndices = async (codigo, reserva, estadoRuta) => {
  const year = reserva.fecha.substring(0, 4), mes = reserva.fecha.substring(5, 7);
  await escribir(`${raiz(codigo)}/reserva_zona/${reserva.zonaId}/${year}/${reserva.id}`, estadoRuta);
  await escribir(`${raiz(codigo)}/reserva_mes/${reserva.zonaId}/${year}/${mes}/${reserva.id}`, estadoRuta);
};

// firebase.dart → crearPaseVisitante(): coordi_ph/{codigo}/visitantes/{tipo}[/{año}/{mes}]
export const crearPaseVisitante = (codigo, pase, year, mes, tipo) => {
  let url = `coordi_ph/${codigo}/visitantes`;
  url = tipo === "TEMPORAL" || tipo === "LIMITADO" ? `${url}/${tipo}/${year}/${mes}` : `${url}/${tipo}`;
  return crear(url, pase);
};
// Enlace reserva → pase (crear_reserva.dart → validarCantidadPasesReservas)
export const enlazarPase = (zona, year, mes, idReserva, i, idPase) =>
  escribir(`reservas/${zona}/${year}/${mes}/${idReserva}/${i}`, idPase);

// firebase.dart → aprobarSolicitud()
export const aprobarSolicitud = async ({ codigo, idConjunto, reserva }) => {
  try {
    reserva.estado = "Autorizado";
    reserva.autorizado = true;
    const json = reservaAJson(reserva);
    const year = reserva.fecha.substring(0, 4), mes = reserva.fecha.substring(5, 7);
    await borrar(`${raiz(codigo)}/reservas/pendientes/${reserva.id}`);
    await escribir(`${raiz(codigo)}/aprobados/${reserva.zonaId}/${reserva.fecha}/${reserva.id}`, json);
    await escribir(`usuarios/${correoClave(reserva.correo)}/reservas/${reserva.zonaId}/${reserva.id}`, json);
    await escribir(`${raiz(codigo)}/${reserva.zonaId}/reservas/${reserva.id}`, json);
    await escribir(`${raiz(codigo)}/reserva_zona/${reserva.zonaId}/${year}/${reserva.id}`, "aprobados");
    await escribir(`${raiz(codigo)}/reserva_mes/${reserva.zonaId}/${year}/${mes}/${reserva.id}`, "aprobados");
    await escribir(`${raiz(codigo)}/reservas/aprobados/${reserva.id}`, json);
  } catch (e) {
    console.error(e);
    return false;
  }
  notificarReserva({ correo: reserva.correo, codigo, idConjunto, tipo: "aprobado", fecha: reserva.fecha, desde: reserva.horaInicio, hasta: reserva.horaFinal, zona: reserva.zona });
  return true;
};

// firebase.dart → rechazarSolicitud()
export const rechazarSolicitud = async ({ codigo, idConjunto, reserva, motivo }) => {
  try {
    reserva.estado = "Rechazado";
    reserva.autorizado = false;
    reserva.motivoRechazo = motivo;
    const json = reservaAJson(reserva);
    const year = reserva.fecha.substring(0, 4), mes = reserva.fecha.substring(5, 7);
    await borrar(`${raiz(codigo)}/reservas/pendientes/${reserva.id}`);
    await escribir(`${raiz(codigo)}/reserva_zona/${reserva.zonaId}/${year}/${reserva.id}`, "rechazados");
    await escribir(`${raiz(codigo)}/reserva_mes/${reserva.zonaId}/${year}/${mes}/${reserva.id}`, "rechazados");
    await escribir(`${raiz(codigo)}/reservas/rechazados/${reserva.id}`, json);
    await escribir(`${raiz(codigo)}/${reserva.zonaId}/reservas/${reserva.id}`, json);
    await escribir(`usuarios/${correoClave(reserva.correo)}/reservas/${reserva.zonaId}/${reserva.id}`, json);
    await ajustarContadoresReserva(codigo, reserva, -1);
  } catch (e) {
    console.error(e);
    return false;
  }
  notificarReserva({ correo: reserva.correo, codigo, idConjunto, tipo: "rechazado", fecha: reserva.fecha, desde: reserva.horaInicio, hasta: reserva.horaFinal, motivo, zona: reserva.zona });
  return true;
};

// firebase.dart → anularReserva(): marca "Anulado" donde la reserva existe, libera el horario
// en aprobados/{zona}/{fecha} y devuelve el cupo.
export const anularReserva = async (codigo, reserva) => {
  try {
    const base = raiz(codigo);
    const correo = correoClave(reserva.correo);
    const bucket = reserva.estado === "Pendiente" ? "pendientes" : "aprobados";
    for (const ruta of [`${base}/reservas/${bucket}/${reserva.id}`, `${base}/${reserva.zonaId}/reservas/${reserva.id}`, `usuarios/${correo}/reservas/${reserva.zonaId}/${reserva.id}`]) {
      if (await leerTexto(`${ruta}/estado`) !== null) await escribir(`${ruta}/estado`, "Anulado");
    }
    const ocupados = await leer(`${base}/aprobados/${reserva.zonaId}/${reserva.fecha}`);
    if (ocupados && typeof ocupados === "object") {
      for (const [clave, v] of Object.entries(ocupados)) {
        if (!v || typeof v !== "object") continue;
        const esLaMisma = clave === reserva.id || v.id === reserva.id ||
          (String(v.id ?? "") === "" && v.correo === reserva.correo && v.horaInicio === reserva.horaInicio && v.horaFinal === reserva.horaFinal);
        if (esLaMisma) await borrar(`${base}/aprobados/${reserva.zonaId}/${reserva.fecha}/${clave}`);
      }
    }
    await ajustarContadoresReserva(codigo, reserva, -1);
    return true;
  } catch (e) {
    console.error(e);
    return false;
  }
};
